import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { api } from "../axios/api";
import { numberFormat } from "../config/Config";
import {
  FileText, CheckCircle2, FileX, ShieldCheck, 
  CreditCard, Award, RefreshCcw, Wallet, TrendingDown
} from "lucide-react";
import Loader from "../components/Loader";

const DashboardDocumentos = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodoAtivo, setPeriodoAtivo] = useState("mensal");

  useEffect(() => {
    const fetchDocData = async () => {
      if (!empresaId) return;
      try {
        setLoading(true);
        const res = await api.get(`/app/dashboard/documentos/${empresaId}`);
        setData(res.data);
      } catch (err) {
        console.error("Erro ao puxar métricas de documentos:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDocData();
  }, [empresaId]);

  if (loading) return <Loader />;

  const { contadores = {}, fluxoFinanceiro = {}, graficos = {}, listas = {} } = data || {};
  // 🌟 Captura as novas propriedades "despesas" e "saldo_caixa" calculadas pela API backend
  const finAtivo = fluxoFinanceiro[periodoAtivo] || { bruto: 0, liquido_real: 0, despesas: 0, saldo_caixa: 0, iva: 0, descontos: 0, retencao: 0 };

  // 💳 Gráfico de Rosca: Apenas valores reais que entraram em Caixa
  const pagamentosOptions = {
    chart: { fontFamily: 'Inter, system-ui, sans-serif' },
    labels: graficos.fluxoMetodosPagamento?.map(p => p.metodo) || [],
    colors: ["#10b981", "#3b82f6", "#f59e0b", "#7c3aed", "#64748b"],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true }
  };

  // 📄 Emissões por Série
  const seriesChartOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    plotOptions: { bar: { columnWidth: '40%', borderRadius: 4 } },
    colors: ["#7c3aed"],
    dataLabels: { enabled: false },
    xaxis: { categories: graficos.emissõesPorSerie?.map(s => s["Serie.nome"] || "Padrão") || [] }
  };

  return (
    <div id="layout-wrapper" className="dashboard-documentos">
      <Navbar />
      <Sidebar />

      <style>{`
        .dashboard-documentos { background-color: #f8fafc; min-height: 100vh; }
        .app-wrapper { padding: 24px; }
        .metric-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; transition: all 0.2s ease; }
        .metric-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
        .period-btn { padding: 6px 16px; font-size: 13px; font-weight: 600; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; transition: all 0.2s; }
        .period-btn.active { background: #7c3aed; color: #fff; border-color: #7c3aed; }
        .card-title-flat { font-size: 13px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
        .table-clean th { font-size: 11px; color: #64748b; text-transform: uppercase; padding: 12px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
        .table-clean td { padding: 14px 16px; font-size: 13px; color: #334155; border-bottom: 1px solid #f1f5f9; }
      `}</style>

      <main className="app-wrapper">
        <div className="container-fluid">
          
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold text-slate-800 m-0" style={{ fontSize: "26px" }}>Painel Integrado de Faturação</h2>
              <small className="text-muted">Monitoria fiscal de documentos comerciais emitidos com dedução automática de retenções na fonte.</small>
            </div>
            
            <div className="d-flex gap-1 bg-white p-1 rounded border">
              <button onClick={() => setPeriodoAtivo("diario")} className={`period-btn ${periodoAtivo === "diario" ? "active" : ""}`}>Hoje</button>
              <button onClick={() => setPeriodoAtivo("semanal")} className={`period-btn ${periodoAtivo === "semanal" ? "active" : ""}`}>Últimos 7 dias</button>
              <button onClick={() => setPeriodoAtivo("mensal")} className={`period-btn ${periodoAtivo === "mensal" ? "active" : ""}`}>Mês Vigente</button>
            </div>
          </div>

          {/* 📊 SEÇÃO 1: RESUMO FINANCEIRO REAL (DEDUZIDO DE RETENÇÃO E DESPESAS) */}
          <div className="row g-3 mb-4">
            {[
              { label: `Saldo Líquido em Caixa`, valor: `${numberFormat(finAtivo.saldo_caixa, 2, ",", ".")} Kz`, icon: <Wallet size={20} />, color: "#10b981" }, // 🌟 Atualizado para refletir Lucro Real
              { label: `Despesas Operacionais`, valor: `${numberFormat(finAtivo.despesas, 2, ",", ".")} Kz`, icon: <TrendingDown size={20} />, color: "#ea580c" }, // 🌟 Novo Card de Saídas
              { label: `Faturamento Líquido Real`, valor: `${numberFormat(finAtivo.liquido_real, 2, ",", ".")} Kz`, icon: <CheckCircle2 size={20} />, color: "#3b82f6" }, // 🌟 Ajustado rótulo
              { label: `Retenções na Fonte (Retido)`, valor: `${numberFormat(finAtivo.retencao, 2, ",", ".")} Kz`, icon: <Award size={20} />, color: "#ef4444" },
            ].map((item, i) => (
              <div key={i} className="col-12 col-sm-6 col-xl-3">
                <div className="metric-card" style={{ borderTop: `4px solid ${item.color}` }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small fw-semibold text-uppercase">{item.label}</span>
                    <div style={{ color: item.color }}>{item.icon}</div>
                  </div>
                  <h3 className="fw-bold m-0 text-slate-700">{item.valor}</h3>
                </div>
              </div>
            ))}
          </div>

          {/* 🛡️ SEÇÃO 2: CONTROLADOR DE CONFORMIDADE */}
          <div className="row g-3 mb-4">
            {[
              { label: "Histórico Total Processado", valor: contadores.totalDocumentos, icon: <FileText size={18} />, badge: "Log Geral", color: "bg-slate-100 text-slate-600" },
              { label: "Documentos Ativos Vigentes", valor: contadores.docsEmitidos, icon: <CheckCircle2 size={18} />, badge: "Válidos", color: "bg-success-subtle text-success" },
              { label: "Assinados & Validados", valor: contadores.submetidosAgt, icon: <ShieldCheck size={18} />, badge: "Sincronizados", color: "bg-blue-100 text-primary" },
              { label: "Taxa de Anulação", valor: `${contadores.taxaAnulacao}%`, icon: <RefreshCcw size={18} />, badge: "Métrica Fiscal", color: "bg-purple-100 text-purple" },
            ].map((card, idx) => (
              <div key={idx} className="col-12 col-sm-6 col-md-3">
                <div className="metric-card bg-white d-flex align-items-center justify-content-between py-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className={`p-2 rounded ${card.color}`}>
                      {card.icon}
                    </div>
                    <div>
                      <span className="text-muted d-block" style={{ fontSize: "11px" }}>{card.label}</span>
                      <span className="fw-bold fs-6 text-slate-800">{card.valor || 0}</span>
                    </div>
                  </div>
                  <span className="badge bg-light text-dark border rounded-sm" style={{ fontSize: '10px' }}>{card.badge}</span>
                </div>
              </div>
            ))}
          </div>

          {/* 📈 SEÇÃO 3: GRÁFICOS */}
          <div className="row g-4 mb-4">
            <div className="col-xl-5">
              <div className="metric-card h-100">
                <div className="mb-3"><span className="card-title-flat">Entrada Real em Caixa por Canal</span></div>
                <div className="d-flex justify-content-center align-items-center h-100">
                  {graficos.fluxoMetodosPagamento && graficos.fluxoMetodosPagamento.length > 0 ? (
                    <ReactApexChart type="donut" width="100%" height={260} series={graficos.fluxoMetodosPagamento.map(p => parseFloat(p.total_acumulado))} options={pagamentosOptions} />
                  ) : (
                    <span className="text-muted small">Sem registos financeiros activos.</span>
                  )}
                </div>
              </div>
            </div>
            <div className="col-xl-7">
              <div className="metric-card h-100">
                <div className="mb-3"><span className="card-title-flat">Movimentação Operacional por Séries</span></div>
                <ReactApexChart type="bar" height={280} series={[{ name: "Quantidade Documentos", data: graficos.emissõesPorSerie?.map(s => parseInt(s.total_docs)) || [] }]} options={seriesChartOptions} />
              </div>
            </div>
          </div>

          {/* 🧾 SEÇÃO 4: AUDITORIA DE DOCUMENTOS */}
          <div className="row g-4">
            {/* Últimos Documentos */}
            <div className="col-xl-6">
              <div className="metric-card p-0 h-100">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                  <span className="card-title-flat m-0">Últimos Documentos Processados</span>
                  <Link to="/documentos" className="btn btn-sm btn-light border fw-semibold text-primary" style={{ fontSize: "11px" }}>Ver Livro Secundário</Link>
                </div>
                <div className="table-responsive">
                  <table className="table-clean w-100 m-0">
                    <thead>
                      <tr>
                        <th>Nº Documento</th>
                        <th>Cliente</th>
                        <th>Estado</th>
                        <th className="text-end">Entrada Líquida</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listas.ultimosDocumentos?.map((doc, i) => {
                        const liquidoRealDoc = parseFloat(doc.total_geral) - parseFloat(doc.retencao_fonte || 0);
                        return (
                          <tr key={i}>
                            <td className="fw-bold text-slate-800">
                              {doc.doc_type} {doc.numero.split('/').pop()}
                            </td>
                            <td>{doc.Cliente?.nome || "Consumidor Final"}</td>
                            <td>
                              <span className={`badge ${doc.estado === 'Emitido' ? 'bg-success-subtle text-success' : doc.estado === 'Anulado' ? 'bg-danger-subtle text-danger' : 'bg-warning-subtle text-warning'}`}>
                                {doc.estado}
                              </span>
                            </td>
                            <td className="text-end fw-bold text-success">
                              {numberFormat(doc.doc_type === "FP" ? 0 : liquidoRealDoc, 2, ",", ".")} Kz
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Maiores Faturas Detalhadas */}
            <div className="col-xl-6">
              <div className="metric-card p-0 h-100">
                <div className="p-3 border-bottom">
                  <span className="card-title-flat m-0 text-primary">👑 Maiores Transações do Mês (Dedução de Retenção)</span>
                </div>
                <div className="table-responsive">
                  <table className="table-clean w-100 m-0">
                    <thead>
                      <tr>
                        <th>Fatura</th>
                        <th className="text-center">Bruto</th>
                        <th className="text-center">Retenção</th>
                        <th className="text-end">Valor Real Caixa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listas.maioresFaturasMes && listas.maioresFaturasMes.length > 0 ? (
                        listas.maioresFaturasMes.map((doc, i) => {
                          const valorBruto = parseFloat(doc.total_geral);
                          const valorRetencao = parseFloat(doc.retencao_fonte || 0);
                          const valorLiquidoReal = valorBruto - valorRetencao;

                          return (
                            <tr key={i}>
                              <td>
                                <span className="fw-semibold d-block text-slate-700">{doc.doc_type} {doc.numero.split('/').pop()}</span>
                                <small className="text-muted" style={{ fontSize: '10px' }}><CreditCard size={10} className="me-1"/>{doc.metodo_pagamento || "Não Definido"}</small>
                              </td>
                              <td className="text-center text-muted">
                                {numberFormat(valorBruto, 2, ",", ".")} Kz
                              </td>
                              <td className="text-center fw-semibold text-danger">
                                {valorRetencao > 0 ? `-${numberFormat(valorRetencao, 2, ",", ".")}` : "0,00"} Kz
                              </td>
                              <td className="text-end fw-bold text-success">
                                {numberFormat(valorLiquidoReal, 2, ",", ".")} Kz
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-4 small">Nenhuma fatura emitida neste período.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </main>
    </div>
  );
};

export default DashboardDocumentos;